type ImportMetaEnv = {
  readonly VITE_API_BASE_URL: string;
  readonly VITE_WS_BASE_URL: string;
  readonly VITE_COGNITO_REGION: string;
  readonly VITE_COGNITO_USER_POOL_ID: string;
  readonly VITE_COGNITO_APP_CLIENT_ID: string;
  readonly VITE_COGNITO_DOMAIN: string;
  readonly VITE_APP_NAME: string;
  readonly VITE_APP_STORE_FIELD_URL: string;
  readonly VITE_APP_STORE_COMMAND_URL: string;
  readonly VITE_E2E_AUTH_ROLE: string;
  readonly VITE_E2E_MOCK_API: string;
  readonly VITE_MIXPANEL_TOKEN: string;
  readonly VITE_MIXPANEL_API_HOST: string;
  readonly VITE_MIXPANEL_DEBUG: string;
  readonly DEV: boolean;
  readonly MODE: string;
};

function read(key: keyof ImportMetaEnv, fallback = ""): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const meta = (import.meta as any).env as ImportMetaEnv | undefined;
  const raw = meta ? meta[key] : undefined;
  return typeof raw === "string" && raw.length > 0 ? raw : fallback;
}

// The E2E auth/mock bypass is a TEST-ONLY backdoor: `readE2EIdentity` (AuthProvider)
// synthesizes a signed-in admin without Cognito, and `e2eMockApi` swaps the real API
// for canned responses. It must NEVER be reachable from a shipped build. The Playwright
// harness runs Vite with `--mode e2e` (see playwright.config.ts); the production build is
// `vite build` (MODE === "production") and local dev is `vite` (MODE === "development").
// Gating both flags on the exact e2e mode means a `VITE_E2E_*` env var that ever leaks
// into a prod/dev build is forced inert here — the auth backdoor can't open and the app
// always talks to the real API. This is the single choke point; do not read those vars
// anywhere else.
// Exact-match the Vite mode so the bypass is OFF for every build except the
// Playwright harness — `"production"`, `"development"`, `"test"`, or any
// unexpected/empty value all return false. Exported so the security invariant
// is unit-tested without depending on `import.meta` (which the test runner
// cannot stub). Do NOT loosen this to `mode !== "production"`: that would arm
// the backdoor in local dev and in any future mode.
export function isE2EHarness(mode: string | undefined): boolean {
  return mode === "e2e";
}

const e2eHarness = isE2EHarness(
  (import.meta as unknown as { env?: ImportMetaEnv }).env?.MODE,
);

export const env = {
  apiBaseUrl: read("VITE_API_BASE_URL", "https://api-dev.respai.ca/v1"),
  wsBaseUrl: read("VITE_WS_BASE_URL", "wss://api-dev.respai.ca/v1"),
  cognitoRegion: read("VITE_COGNITO_REGION", "ca-central-1"),
  cognitoUserPoolId: read("VITE_COGNITO_USER_POOL_ID"),
  cognitoAppClientId: read("VITE_COGNITO_APP_CLIENT_ID"),
  cognitoDomain: read("VITE_COGNITO_DOMAIN"),
  appName: read("VITE_APP_NAME", "RespAI Admin"),
  appStoreFieldUrl: read(
    "VITE_APP_STORE_FIELD_URL",
    "https://apps.apple.com/ca/app/respai-field/",
  ),
  appStoreCommandUrl: read(
    "VITE_APP_STORE_COMMAND_URL",
    "https://apps.apple.com/ca/app/respai-command/",
  ),
  e2eAuthRole: e2eHarness ? read("VITE_E2E_AUTH_ROLE") : "",
  e2eMockApi: e2eHarness && read("VITE_E2E_MOCK_API") === "true",
  // Product analytics (Mixpanel). Empty token → analytics is a no-op, so the
  // dashboard runs identically in local dev / CI without sending any events.
  mixpanelToken: read("VITE_MIXPANEL_TOKEN"),
  mixpanelApiHost: read("VITE_MIXPANEL_API_HOST"),
  mixpanelDebug: read("VITE_MIXPANEL_DEBUG") === "true",
};
